import { format } from 'date-fns';
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit2,
  ExternalLink,
  MapPin,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { CitationViewer, type EvidenceData } from '@/components/citations';
import { Button } from '@/components/ui/catalyst/button';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import type { IncidentSchema } from '@/services/types/incident-types';

import { IncidentEditForm } from './IncidentEditForm';
import { IncidentImageCarousel } from './IncidentImageCarousel';
import { IncidentLocationSection } from './IncidentLocationSection';

interface IncidentDetailsPanelProps {
  matterId: string;
  incident: IncidentSchema | null | undefined;
  onSaved?: () => void;
}

/**
 * Format date with day of week
 */
const formatDateWithDay = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return format(date, 'EEE, MMM d, yyyy');
  } catch {
    return dateStr;
  }
};

/**
 * Get normalization status display
 */
const getNormalizationStatusDisplay = (
  status: string | null | undefined,
  confidence: number | null | undefined,
) => {
  if (!status) return null;

  switch (status) {
    case 'success':
      return null;
    case 'low_confidence':
      return {
        icon: AlertCircle,
        text: `Low confidence${confidence ? ` (${Math.round(confidence * 100)}%)` : ''}`,
        className: 'text-amber-600',
      };
    case 'failed':
      return {
        icon: XCircle,
        text: 'Could not normalize',
        className: 'text-red-600',
      };
    case 'pending':
      return {
        icon: Clock,
        text: 'Normalizing...',
        className: 'text-gray-500',
      };
    default:
      return null;
  }
};

const Field = ({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) => {
  if (!value) return null;
  return (
    <div className="py-1 border-b border-gray-100 last:border-0">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-xs text-gray-900 mt-0.5">{value}</dd>
    </div>
  );
};

export const IncidentDetailsPanel = ({
  matterId,
  incident,
  onSaved,
}: IncidentDetailsPanelProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [showInjuries, setShowInjuries] = useState(false);
  const [showMedical, setShowMedical] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  // Fetch incident with mentions for evidence
  const {
    data: incidents,
    isLoading: isIncidentLoading,
    error: incidentError,
  } = useGetIncidentsQuery({ matterId }, { skip: !incident });

  const incidentWithMentions = incidents?.[0];
  const hasEvidence =
    incidentWithMentions?.mentions && incidentWithMentions.mentions.length > 0;

  // Convert mentions to evidence data format
  const evidenceData: EvidenceData[] =
    incidentWithMentions?.mentions?.map((mention) => ({
      artifact_id: mention.artifact_id,
      citation_ids: mention.citation_ids || [],
      confidence: mention.confidence || 0,
      context: mention.context || '',
      created_at: mention.created_at,
      artifact_type: mention.artifact_type,
      rationale: null,
    })) || [];

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const handleEditSave = () => {
    setIsEditing(false);
    toast.success('Accident details updated');
    onSaved?.();
  };

  const handleImageClick = () => {
    if (hasLocationImages) {
      setIsCarouselOpen(true);
    }
  };

  // Extract location data
  const location = incident?.normalized_location;
  const hasLocationImages =
    location?.satellite_image_url ||
    location?.streetview_image_url_0 ||
    location?.streetview_image_url_90 ||
    location?.streetview_image_url_180 ||
    location?.streetview_image_url_270;

  const statusDisplay = getNormalizationStatusDisplay(
    location?.normalization_status,
    location?.normalization_confidence,
  );

  // Check if we have any content to show
  const hasAnyContent =
    incident?.date_of_loss ||
    incident?.time_of_loss ||
    incident?.incident_type ||
    incident?.location ||
    incident?.description ||
    incident?.weather_conditions ||
    incident?.injuries_described ||
    incident?.hospital ||
    incident?.ambulance ||
    incident?.current_treatment ||
    incident?.police_report ||
    incident?.witnesses ||
    incident?.statements ||
    incident?.photos;

  if (!incident) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs text-gray-500 italic">No incident data yet.</p>
      </div>
    );
  }

  if (isEditing) {
    return (
      <IncidentEditForm
        matterId={matterId}
        incident={incident}
        onSave={handleEditSave}
        onCancel={handleEditCancel}
      />
    );
  }

  const hasMedical =
    incident.hospital || incident.ambulance || incident.current_treatment;
  const hasDocs = incident.witnesses || incident.statements || incident.photos;

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      {hasLocationImages && (
        <IncidentLocationSection
          location={location}
          onClick={handleImageClick}
        />
      )}

      <div className="bg-muted/20 border-b rounded-t-lg flex items-center justify-between p-1.5">
        <h3 className="text-xs font-medium text-gray-900 px-1">Accident Details</h3>
        <div className="flex items-center gap-2">
          {hasEvidence && (
            <CitationViewer
              evidence={evidenceData}
              matterId={matterId}
              dialogTitle="Incident Evidence Sources"
              isLoading={isIncidentLoading}
              error={!!incidentError}
              errorMessage="Unable to load evidence sources"
              buttonClassName="text-xs text-slate-500 hover:text-slate-900"
            />
          )}
          <Button plain onClick={handleEditClick}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {(incident.date_of_loss || incident.incident_type) && (
          <div className="flex items-center gap-2 text-xs">
            {incident.date_of_loss && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3 text-gray-400" />
                <span className="font-medium text-gray-900">
                  {formatDateWithDay(incident.date_of_loss)}
                </span>
                {incident.time_of_loss && (
                  <span className="text-gray-500">{incident.time_of_loss}</span>
                )}
              </div>
            )}
            {incident.incident_type && (
              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                {incident.incident_type}
              </span>
            )}
          </div>
        )}

        {(incident.location || location?.formatted_address) && (
          <div className="flex items-start gap-1.5">
            <MapPin className="h-3 w-3 text-gray-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 text-xs">
              <p className="text-gray-900">
                {location?.formatted_address || incident.location}
              </p>
              {incident.cross_street && (
                <p className="text-xs text-gray-500">
                  at {incident.cross_street}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                {incident.weather_conditions && (
                  <span className="text-gray-500">
                    {incident.weather_conditions}
                  </span>
                )}
                {statusDisplay && (
                  <span
                    className={`flex items-center gap-1 ${statusDisplay.className}`}
                  >
                    <statusDisplay.icon className="h-3 w-3" />
                    {statusDisplay.text}
                  </span>
                )}
                {location?.google_maps_uri && (
                  <a
                    href={location.google_maps_uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-0.5 text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Maps
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {incident.description && (
          <p className="text-xs text-gray-900 whitespace-pre-wrap">
            {incident.description}
          </p>
        )}

        {incident.police_report && (
          <div className="text-xs">
            <span className="text-xs font-medium text-gray-500">Police Report</span>
            <p className="text-gray-900 mt-0.5">{incident.police_report}</p>
          </div>
        )}

        {incident.injuries_described && (
          <div>
            <button
              type="button"
              onClick={() => setShowInjuries(!showInjuries)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 w-full"
            >
              {showInjuries ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              Injuries
            </button>
            {showInjuries && (
              <div className="mt-1 border-l-2 border-red-200 pl-2">
                <p className="text-xs text-gray-900 whitespace-pre-wrap">
                  {incident.injuries_described}
                </p>
              </div>
            )}
          </div>
        )}

        {hasMedical && (
          <div>
            <button
              type="button"
              onClick={() => setShowMedical(!showMedical)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 w-full"
            >
              {showMedical ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              Medical Response
            </button>
            {showMedical && (
              <dl className="mt-1 space-y-0.5 bg-gray-50 rounded px-2 py-1">
                <Field label="Hospital" value={incident.hospital} />
                <Field label="Ambulance" value={incident.ambulance} />
                <Field label="Treatment" value={incident.current_treatment} />
              </dl>
            )}
          </div>
        )}

        {hasDocs && (
          <div>
            <button
              type="button"
              onClick={() => setShowDocs(!showDocs)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 w-full"
            >
              {showDocs ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              Documentation
            </button>
            {showDocs && (
              <dl className="mt-1 space-y-0.5 bg-gray-50 rounded px-2 py-1">
                <Field label="Witnesses" value={incident.witnesses} />
                <Field label="Statements" value={incident.statements} />
                <Field label="Photos/Evidence" value={incident.photos} />
              </dl>
            )}
          </div>
        )}

        {!hasAnyContent && (
          <p className="text-xs text-gray-500 italic">
            No accident details recorded yet.
          </p>
        )}
      </div>

      <IncidentImageCarousel
        location={location}
        isOpen={isCarouselOpen}
        onClose={() => setIsCarouselOpen(false)}
      />
    </div>
  );
};
